import {
  AppointmentStatusCodes,
  CaseDto,
  CaseStatus,
  DataBlockDictionaryDto,
  DataBlockDictionaryItemDto,
  DataBlockFieldDto,
  DataType,
  PatientDto,
  VisitDto,
} from '@lmt/shared-types';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export type IHardcodedField = {
  customValuesFieldName: string;
  dictionaryName?: string | null;
  fieldType: DataType;
  isRequired?: boolean;

  /*
   * Some fields are readonly / should not be changed via Custom Values (e.g. Patient.LastChangeDateTime).
   * These values are copied FROM Patient to Custom Values, but NOT vice versa
   */
  readOnly?: boolean;
};

// backend: SharedDataBlockFieldsConstants.cs
export const patientHardcodedFields: Record<
  Exclude<
    keyof PatientDto,
    | 'id'
    | 'tags'
    | 'values'
    | 'importErrors'
    | 'subtenantId'
    | 'isDeleted'
    | 'anonymous'
    | 'priorityContact'
    | 'priorityContactType'
    | 'importId'
    | 'deletionReason'
    | 'lastChangeDateTime'
  >,
  IHardcodedField
> = {
  number: {
    customValuesFieldName: 'Number',
    fieldType: DataType.Text,
    isRequired: true,
  },
  firstName: {
    customValuesFieldName: 'FirstName',
    fieldType: DataType.Text,
    isRequired: true,
  },
  lastName: {
    customValuesFieldName: 'LastName',
    fieldType: DataType.Text,
    isRequired: true,
  },
  sex: {
    customValuesFieldName: 'Sex',
    fieldType: DataType.Dictionary,
    dictionaryName: 'dictionaries.sex_types._title',
  },
  dateOfBirth: {
    customValuesFieldName: 'DateOfBirth',
    fieldType: DataType.DateOnly,
    isRequired: true,
  },
  numberSource: {
    customValuesFieldName: 'NumberSource',
    readOnly: true,
    fieldType: DataType.Text,
  },
  address: {
    customValuesFieldName: 'Address',
    fieldType: DataType.Text,
  },
  postcode: {
    customValuesFieldName: 'Postcode',
    fieldType: DataType.Text,
  },
  location: {
    customValuesFieldName: 'Location',
    fieldType: DataType.Text,
  },
  homePhone: {
    customValuesFieldName: 'HomePhone',
    fieldType: DataType.Text,
  },
  workPhone: {
    customValuesFieldName: 'WorkPhone',
    fieldType: DataType.Text,
  },
  mobilePhone: {
    customValuesFieldName: 'MobilePhone',
    fieldType: DataType.Text,
  },
  fax: {
    customValuesFieldName: 'Fax',
    fieldType: DataType.Text,
  },
  email: {
    customValuesFieldName: 'Email',
    fieldType: DataType.Text,
  },
  emailsAllowed: {
    customValuesFieldName: 'EmailsAllowed',
    fieldType: DataType.Boolean,
  },
  healthInsuranceCompanyId: {
    customValuesFieldName: 'HealthInsuranceCompany',
    fieldType: DataType.Dictionary,
    dictionaryName: 'dictionaries.insurance_companies._title',
  },
  isPrivateInsurance: {
    customValuesFieldName: 'IsPrivateInsurance',
    fieldType: DataType.Boolean,
  },
  isSelfPayer: {
    customValuesFieldName: 'IsSelfPayer',
    fieldType: DataType.Boolean,
  },
  insuranceNumber: {
    customValuesFieldName: 'InsuranceNumber',
    fieldType: DataType.Text,
  },
  doctorId: {
    customValuesFieldName: 'Doctor',
    fieldType: DataType.Dictionary,
    dictionaryName: 'Doctors',
  },
  comments: {
    customValuesFieldName: 'Comments',
    fieldType: DataType.Text,
  },
  isDeceased: {
    customValuesFieldName: 'IsDeceased',
    fieldType: DataType.Boolean,
  },
  dateOfDeath: {
    customValuesFieldName: 'DateOfDeath',
    fieldType: DataType.DateOnly,
  },
};

export const caseHardcodedFields: Record<
  Exclude<
    keyof CaseDto,
    'id' | 'patientId' | 'values' | 'patientId' | 'isClosed'
  >,
  IHardcodedField
> = {
  number: {
    customValuesFieldName: 'Number',
    fieldType: DataType.Text,
    isRequired: true,
  },
  numberSource: {
    customValuesFieldName: 'NumberSource',
    fieldType: DataType.Text,
    readOnly: true,
  },
  startDate: {
    customValuesFieldName: 'StartDate',
    fieldType: DataType.DateOnly,
    isRequired: true,
  },
  endDate: {
    customValuesFieldName: 'EndDate',
    fieldType: DataType.DateOnly,
  },
  status: {
    customValuesFieldName: 'Status',
    fieldType: DataType.Dictionary,
    dictionaryName: 'CaseStatus',
    isRequired: true,
  },
};

export const visitHardcodedFields: Record<
  Exclude<
    keyof VisitDto,
    | 'id'
    | 'patientId'
    | 'values'
    | 'isClosed'
    | 'tags'
    | 'subtenantId'
    | 'isDeleted'
    | 'participants'
    | 'resourceIds'
    | 'patientName'
    | 'typeId'
    | 'caseId'
    | 'attendingDoctorId'
    | 'inWaitingList'
    | 'lastPatientNotificationDateTime'
    | 'priority'
    | 'importId'
    | 'isPrivateInsurance'
    | 'isSelfPayer'
    | 'title'
  >,
  IHardcodedField
> = {
  number: {
    customValuesFieldName: 'Number',
    fieldType: DataType.Text,
    isRequired: true,
  },
  numberSource: {
    customValuesFieldName: 'NumberSource',
    readOnly: true,
    fieldType: DataType.Text,
  },
  note: {
    customValuesFieldName: 'Note',
    fieldType: DataType.Text,
  },
  start: {
    customValuesFieldName: 'Start',
    fieldType: DataType.DateTime,
    isRequired: true,
  },
  end: {
    customValuesFieldName: 'End',
    fieldType: DataType.DateTime,
    isRequired: true,
  },
  statusId: {
    customValuesFieldName: 'Status',
    fieldType: DataType.Dictionary,
    dictionaryName: 'VisitStatus',
    isRequired: true,
  },
};
type HardcodedDictionariesList = 'CaseStatus' | 'VisitStatus' | 'VisitPriority';
export const hardcodedDictionaries: Record<string, DataBlockDictionaryDto> = {
  CaseStatus: {
    id: -1,
    title: 'CaseStatus',
    isDefault: true,
    isDeleted: false,
    isEditable: false,
    isExternal: true,
    isReadOnly: false,
    columns: [],
    manualOrdering: false,
    isMandatory: true,
  },
  VisitStatus: {
    id: -2,
    title: 'VisitStatus',
    isDefault: true,
    isDeleted: false,
    isEditable: false,
    isExternal: true,
    isReadOnly: false,
    columns: [],
    manualOrdering: false,
    isMandatory: true,
  },
  VisitPriority: {
    id: -3,
    title: 'VisitPriority',
    isDefault: true,
    isDeleted: false,
    isEditable: false,
    isExternal: true,
    isReadOnly: false,
    columns: [],
    manualOrdering: false,
    isMandatory: true,
  },
} as Record<HardcodedDictionariesList, DataBlockDictionaryDto>;

export const hardcodedDictionaryNameById: Record<
  number,
  HardcodedDictionariesList
> = Object.fromEntries(
  Object.keys(hardcodedDictionaries).map((key) => [
    hardcodedDictionaries[key].id,
    key as HardcodedDictionariesList,
  ]),
);

const _hardcodedDictionariesLanguageCache: Record<
  string,
  Record<HardcodedDictionariesList, DataBlockDictionaryItemDto[]>
> = {};
export function useHardcodedDictionaryItems(): Record<
  HardcodedDictionariesList,
  DataBlockDictionaryItemDto[]
> {
  const i18next = useTranslation();
  return useMemo(
    () =>
      _hardcodedDictionariesLanguageCache[i18next.i18n.language] ??
      (_hardcodedDictionariesLanguageCache[i18next.i18n.language] = {
        CaseStatus: [
          {
            dictionaryId: -1,
            id: CaseStatus.Canceled,
            isDeleted: false,
            value: i18next.t('dictionaries.case_status.canceled'),
            manualOrderPosition: -1,
          },
          {
            dictionaryId: -1,
            id: CaseStatus.Completed,
            isDeleted: false,
            value: i18next.t('dictionaries.case_status.completed'),
            manualOrderPosition: -1,
          },
          {
            dictionaryId: -1,
            id: CaseStatus.Normal,
            isDeleted: false,
            value: i18next.t('dictionaries.case_status.normal'),
            manualOrderPosition: -1,
          },
          {
            dictionaryId: -1,
            id: CaseStatus.Planned,
            isDeleted: false,
            value: i18next.t('dictionaries.case_status.planned'),
            manualOrderPosition: -1,
          },
        ],
        VisitStatus: [
          {
            dictionaryId: -2,
            id: AppointmentStatusCodes.Planned,
            isDeleted: false,
            value: i18next.t('visit_status.1'),
            manualOrderPosition: -1,
          },
          {
            dictionaryId: -2,
            id: AppointmentStatusCodes.Skipped,
            isDeleted: false,
            value: i18next.t('visit_status.2'),
            manualOrderPosition: -1,
          },
          {
            dictionaryId: -2,
            id: AppointmentStatusCodes.Canceled,
            isDeleted: false,
            value: i18next.t('visit_status.3'),
            manualOrderPosition: -1,
          },
          {
            dictionaryId: -2,
            id: AppointmentStatusCodes.Completed,
            isDeleted: false,
            value: i18next.t('visit_status.4'),
            manualOrderPosition: -1,
          },
          {
            dictionaryId: -2,
            id: AppointmentStatusCodes.Confirmed,
            isDeleted: false,
            value: i18next.t('visit_status.5'),
            manualOrderPosition: -1,
          },
        ],
        VisitPriority: [
          {
            dictionaryId: -3,
            id: 10,
            isDeleted: false,
            value: '1',
            manualOrderPosition: -1,
          },
          {
            dictionaryId: -2,
            id: 20,
            isDeleted: false,
            value: '2',
            manualOrderPosition: -1,
          },
          {
            dictionaryId: -2,
            id: 30,
            isDeleted: false,
            value: '3',
            manualOrderPosition: -1,
          },
        ],
      }),
    [i18next.i18n.language],
  );
}

export function getHardcodedFieldsConfiguration(
  config: Record<string, IHardcodedField>,
): DataBlockFieldDto[] {
  return Object.values(config).map((field) => {
    return {
      dataType: field.fieldType,
      name: field.customValuesFieldName,
      isDeactivated: false,
      id: NaN,
    } as DataBlockFieldDto;
  });
}

export type ExtendedFieldsConfigurationField = DataBlockFieldDto & {
  dictionaryTitle?: string | undefined | null;
  isRequired?: boolean;
};
export function useHardcodedFieldsConfiguration(
  config: Record<string, IHardcodedField>,
  options?: { enabled?: boolean },
): ExtendedFieldsConfigurationField[] {
  return useMemo(() => {
    if (options?.enabled === false) return [];
    return Object.values(config).map((field) => {
      const result = {
        dataType: field.fieldType,
        name: field.customValuesFieldName,
        isDeactivated: false,
        id: NaN,
        isSingleValue: true,
        isOptionAllowed: false,
        isRequired: field.isRequired,
      } as ExtendedFieldsConfigurationField;
      if (field.dictionaryName) {
        result.dictionaryTitle = field.dictionaryName;
      }
      return result;
    });
  }, [config, options?.enabled]);
}
